import React from 'react';
import { motion } from 'framer-motion';
import { FloppyDisk, Trash } from '@phosphor-icons/react';

/**
 * Post-stream Save Recording modal. Purely presentational.
 * Parent owns all state; this only renders + fires callbacks.
 */
const SaveRecordingDialog = ({
  open,
  uploading,
  uploadProgress,
  recordingDuration,
  vodAccess, setVodAccess,
  vodPrice, setVodPrice,
  onSave,
  onDiscard,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="save-recording-dialog">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto overscroll-contain"
      >
        <h2 className="font-heading text-xl font-bold text-white mb-2">Stream Ended</h2>
        <p className="text-slate-400 text-sm mb-4">
          Your local recording is ready ({recordingDuration > 0 ? `${Math.floor(recordingDuration / 60)}m ${recordingDuration % 60}s` : 'processing...'}).
          Only you can save or discard it — viewers had no ability to record your stream. Publish it as a replay for your audience?
        </p>

        {!uploading && (
          <div className="mb-5 p-4 bg-slate-800/50 rounded-xl space-y-3" data-testid="vod-pricing">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Access</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'free', label: 'Free' },
                { id: 'paid', label: 'Paid' },
                { id: 'subscribers_only', label: 'Subs Only' },
              ].map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setVodAccess(o.id)}
                  className={`py-2 text-xs font-bold rounded-lg transition-colors ${
                    vodAccess === o.id ? 'bg-blue-500 text-white' : 'bg-slate-700/60 text-slate-300 hover:bg-slate-700'
                  }`}
                  data-testid={`vod-access-${o.id}`}
                >{o.label}</button>
              ))}
            </div>
            {vodAccess === 'paid' && (
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Price (tokens)</label>
                <input
                  type="number" min={1} max={10000}
                  value={vodPrice}
                  onChange={(e) => setVodPrice(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full mt-1 bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-white"
                  data-testid="vod-price-input"
                />
              </div>
            )}
          </div>
        )}

        {uploading ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              Uploading... {uploadProgress}%
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2">
              <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        ) : (
          <div className="flex gap-3">
            <button
              onClick={onSave}
              className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 flex items-center justify-center gap-2"
              data-testid="save-recording-btn"
            >
              <FloppyDisk weight="fill" className="w-5 h-5" /> Save Replay
            </button>
            <button
              onClick={onDiscard}
              className="flex-1 py-3 bg-slate-800 text-slate-300 rounded-xl font-bold hover:bg-slate-700 flex items-center justify-center gap-2"
              data-testid="discard-recording-btn"
            >
              <Trash className="w-5 h-5" /> Discard
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SaveRecordingDialog;
